// =====================================================
// Role context — manages current user role
// =====================================================
//
// Phase-14.5 C1: dropped @ts-nocheck. This file is the RBAC type
// contract that the rest of the app (sidenav, shared.tsx Nav,
// ui.tsx role-switcher, CASL abilities) depends on.
//
// Phase-15 R7: extended the role catalogue from 5 to 10. The
// `student`, `instructor`, `admin`, `parent`, `org` entries stay
// shaped exactly as before. The new five — `ta`, `content_manager`,
// `support`, `moderator`, `super_admin` — get their own ROLES entry
// plus a tailored `nav` list so the sidebar surfaces only the routes
// each capability set can actually use. Display names + avatars are
// placeholders the api can later override per logged-in user.
//
// The RoleId union is the source of truth — TypeScript catches every
// switch / lookup that forgot to handle a new entry. Adjacent ability
// rules live in apps/web/src/auth/ability.ts and the api's
// `AbilityFactory`.
//
// R-NAV-1 (spec §2.0/§2.7/§22.6): the independent localStorage role is
// GONE. `RoleProvider` now DERIVES the active role from `useAuth()` —
// the real session is the single source of truth. localStorage holds at
// most a *preference* (which of a multi-role user's roles is active),
// always re-validated against the live session at render time, so a
// stale value can never grant a role the current user lacks. Anonymous
// visitors have NO active role (activeRoleId === null) → no role
// identity leaks into the shell (the "logout looks logged-in" bug).
// =====================================================
import React from "react";
import { useAuth } from "./auth/AuthContext";
import { apiRolesToLocal } from "./auth/role-map";
// S-I18N-W1a — the non-hook translator seam (see ./i18n/locale-state).
import { translate } from "./i18n/locale-state";

export type RoleId =
  | "student"
  | "instructor"
  | "admin"
  | "faculty_admin"
  | "parent"
  | "org"
  | "ta"
  | "content_manager"
  | "support"
  | "moderator"
  | "super_admin"
  | "finance_manager"
  | "counselor"
  // S-CARD فاز A W1 — دو نقشِ میزِ عضویت. عمداً همین‌جا (نه فقط در بک‌اند)
  // افزوده شدند: افزودنِ نقش فقط در بک‌اند یک‌بار باعث شد کاربرانِ finance_manager
  // به سایدبارِ دانشجو سقوط کنند (tests/auth/role-map.test.ts:1-6).
  | "membership_operator"
  | "membership_manager"
  // S-ASSESS-W3a — کنسولِ ارزیاب. زیرو-UI در این موج (فقط API)؛ عمداً همین‌جا
  // (نه فقط بک‌اند) افزوده شدند تا کاربرانِ این دو نقش به سایدبارِ دانشجو
  // سقوط نکنند — همان تلهٔ membership_operator/membership_manager بالا.
  | "assessor"
  | "assessment_manager";

// Permissions are deliberately a precise union (not `string`) so a
// new permission added later surfaces in every <Can> check that
// hasn't accounted for it. Phase 15 R7 keeps these coarse flags
// alongside the per-action CASL abilities — both layers are useful
// (RBAC flags for quick nav decisions, CASL for record-level checks).
export type RolePermission =
  // student
  | "learn"
  | "submit"
  | "discuss"
  | "view-grades"
  // instructor
  | "teach"
  | "grade"
  | "approve-ai"
  | "create-content"
  // admin
  | "manage-users"
  | "moderate"
  | "configure"
  | "billing"
  // faculty_admin (scope-bound to one faculty)
  | "manage-faculty"
  // parent
  | "view-child"
  | "view-bills"
  | "communicate"
  // org
  | "manage-team"
  | "view-progress"
  // ta
  | "tutor-students"
  // content_manager
  | "publish-content"
  // support
  | "audit-read"
  | "reset-passwords"
  // moderator
  | "moderate-discussions"
  // super_admin
  | "cross-tenant"
  // counselor (مشاور دانشگاه) — least-privilege wellness role: sees only its own
  // appointments, the tenant's open wellness crisis queue (duty-of-care), and a
  // SCOPED wellness summary for a student it has an appointment / open alert for.
  | "counsel"
  // S-CARD فاز A W1 — رجیستریِ عضویت. یک پرچمِ تازه و نه بازاستفادهٔ
  // `manage-users`: آن پرچم نوارِ مدیریتِ کاربران را باز می‌کند و این دو نقش
  // ادمین نیستند. این پرچمِ RBAC خودش را هیچ <Can> نمی‌خواند، اما از
  // S-HYGIENE-1 H4 صفحهٔ صفِ عضویت یک <Can I="update" a="Membership">ِ
  // واقعی (لایهٔ CASL) دارد — auth/Can.tsx + authz/ability.factory.ts.
  | "manage-membership"
  // S-ASSESS-W3a — کنسولِ ارزیاب. زیرو-UI؛ هیچ <Can> این پرچم را نمی‌خواند.
  | "manage-assessment";

export interface Role {
  /** Stable identifier; matches the key in ROLES. */
  id: RoleId;
  /** Persian display label ("دانشجو", "استاد", ...). */
  label: string;
  /** Display name of the seed user for this role. */
  name: string;
  /** Two-letter avatar initials. */
  avatar: string;
  /** CSS token name for the role colour ("cyan", "amber", "violet", "rose"). */
  color: string;
  /** One-line role context (department / cohort / etc.). */
  subtitle: string;
  /** Mock identifier shown under the name. */
  code: string;
  /** Route ids visible in the top nav for this role. */
  nav: string[];
  /** Route id rendered after login for this role. */
  homeRoute: string;
  /** Coarse-grained capability flags; pairs with CASL abilities. */
  permissions: RolePermission[];
}

export const ROLES: Record<RoleId, Role> = {
  student: {
    id: "student",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.student", "دانشجو"); },
    name: "نسرین رضوی",
    avatar: "نر",
    color: "cyan",
    subtitle: "ارشد علوم داده",
    code: "ST-84-02-17",
    nav: ["home", "catalog", "my-courses", "progress", "tutor", "calendar", "community"],
    homeRoute: "progress",
    permissions: ["learn", "submit", "discuss", "view-grades"],
  },
  instructor: {
    id: "instructor",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.instructor", "استاد"); },
    name: "دکتر آرش عظیمی",
    avatar: "AA",
    color: "amber",
    subtitle: "دانشکده علوم رایانه",
    code: "FA-09-A47",
    nav: ["home", "catalog", "instructor", "admin/course-content", "classroom", "calendar", "analytics", "tutor"],
    homeRoute: "instructor-dashboard",
    permissions: ["teach", "grade", "approve-ai", "create-content"],
  },
  admin: {
    id: "admin",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.admin", "ریاست دانشگاه"); },
    name: "احمد محمدی",
    avatar: "AM",
    color: "violet",
    subtitle: "ریاست دانشگاه",
    code: "AD-001",
    nav: ["home", "catalog", "progress", "admin", "analytics", "audit", "tutor", "events"],
    homeRoute: "admin",
    permissions: ["manage-users", "moderate", "configure", "billing"],
  },
  // R-NAV-2 — 11th role (spec §2.2/§2.3/§8.0). Scope-bound to one faculty.
  // `name`/`subtitle`/`code` are neutral fallbacks only — the shell shows the
  // real authed user's name and gates identity on auth (see role.tsx header).
  faculty_admin: {
    id: "faculty_admin",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.faculty_admin", "ریاست دانشکده"); },
    name: "ریاست دانشکده",
    avatar: "رد",
    color: "cyan",
    subtitle: "ریاست دانشکده",
    code: "FAD-001",
    nav: ["faculty-dashboard", "admin/departments", "admin/programs", "admin/offerings", "admin/enrollments"],
    homeRoute: "faculty-dashboard",
    permissions: ["manage-faculty", "configure"],
  },
  parent: {
    id: "parent",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.parent", "والد"); },
    name: "محمد رضوی",
    avatar: "MR",
    color: "rose",
    subtitle: "والد · نسرین رضوی",
    code: "PR-84-02",
    nav: ["home", "parent", "calendar", "credential", "help"],
    homeRoute: "parent",
    permissions: ["view-child", "view-bills", "communicate"],
  },
  org: {
    id: "org",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.org", "سازمان"); },
    name: "شرکت دانش‌بنیان فردا",
    avatar: "DF",
    color: "amber",
    subtitle: "Enterprise · ۱۴۰ کاربر",
    code: "ORG-2026-DF",
    nav: ["home", "admin", "analytics", "events", "pricing", "help"],
    // Phase-A R3: dedicated Org Manager dashboard at /org.
    homeRoute: "org",
    permissions: ["manage-team", "view-progress", "billing"],
  },
  ta: {
    id: "ta",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.ta", "دستیار آموزشی"); },
    name: "سینا کریمی",
    avatar: "SK",
    color: "cyan",
    subtitle: "دستیار · علوم داده",
    code: "TA-2026-07",
    nav: ["home", "catalog", "my-courses", "classroom", "calendar", "progress", "tutor"],
    // Phase-A R3: dedicated TA dashboard at /ta replaces the old classroom landing.
    homeRoute: "ta",
    permissions: ["teach", "grade", "tutor-students"],
  },
  content_manager: {
    id: "content_manager",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.content_manager", "مدیر محتوا"); },
    name: "ندا رحمانی",
    avatar: "NR",
    color: "amber",
    subtitle: "تیم تولید محتوا",
    code: "CM-2026-03",
    nav: ["home", "catalog", "admin/course-content", "analytics", "help"],
    // Phase-A R3: dedicated Content Manager dashboard at /content.
    homeRoute: "content",
    permissions: ["create-content", "publish-content"],
  },
  support: {
    id: "support",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.support", "پشتیبانی"); },
    name: "حسین مرادی",
    avatar: "HM",
    color: "rose",
    subtitle: "پشتیبانی فنی",
    code: "SUP-2026-12",
    nav: ["home", "admin", "audit", "messages", "help"],
    // Phase-A R3: dedicated Support desk at /support.
    homeRoute: "support",
    permissions: ["audit-read", "reset-passwords"],
  },
  moderator: {
    id: "moderator",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.moderator", "ناظر"); },
    name: "زهرا فرجی",
    avatar: "ZF",
    color: "violet",
    subtitle: "ناظر انجمن‌ها",
    code: "MOD-2026-05",
    nav: ["home", "community", "messages", "help"],
    // Phase-A R3: dedicated Moderator queue at /moderate.
    homeRoute: "moderate",
    permissions: ["moderate-discussions", "moderate"],
  },
  // Backend role «مدیر مالی» — payroll/treasury/decree access. Like
  // faculty_admin it is a staff/admin-tier role: `name`/`subtitle`/`code`
  // are neutral fallbacks; the shell shows the real authed user. Reuses the
  // existing finance-ish flags `billing` + `configure` (same pair admin uses
  // for finance) — no new permission strings invented.
  finance_manager: {
    id: "finance_manager",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.finance_manager", "مدیر مالی"); },
    name: "مدیر مالی",
    avatar: "مم",
    color: "amber",
    subtitle: "مدیریت مالی و حقوق",
    code: "FIN-001",
    nav: [
      "admin/finance/treasury",
      "admin/finance/payroll",
      "admin/finance/decrees",
      "admin/finance/claims",
      "admin/tuition-config",
    ],
    homeRoute: "admin/finance/treasury",
    permissions: ["billing", "configure"],
  },
  // WELL-4 — «مشاور دانشگاه». LEAST-PRIVILEGE wellness role: its workspace is the
  // counselor inbox; it sees ONLY its own appointments, the tenant's open wellness
  // crisis queue (the owner's duty-of-care decision), and a SCOPED student wellness
  // summary for a student it has an appointment / open alert for — NOT the whole
  // student record, NOT academic/finance data. `name`/`subtitle`/`code` are neutral
  // fallbacks; the shell shows the real authed user.
  counselor: {
    id: "counselor",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.counselor", "مشاور دانشگاه"); },
    name: "مشاور دانشگاه",
    avatar: "مش",
    color: "rose",
    subtitle: "مرکز مشاوره و سلامت روان",
    code: "CNS-001",
    nav: ["counselor-inbox", "counseling-appointments", "crisis-queue", "profile", "settings"],
    homeRoute: "counselor-inbox",
    permissions: ["counsel", "communicate"],
  },
  // R2-009 — role homes use the implemented work consoles; profile remains reachable.
  membership_operator: {
    id: "membership_operator",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.membership_operator", "اپراتور عضویت"); },
    name: "اپراتور عضویت",
    avatar: "اع",
    color: "cyan",
    subtitle: "میز عضویت",
    code: "MBR-001",
    nav: ["admin/membership-queue", "profile", "settings"],
    homeRoute: "admin/membership-queue",
    permissions: ["manage-membership"],
  },
  // S-CARD فاز A W1 — «مدیر برنامهٔ عضویت». افزون بر خواندن/تأیید: تعلیق،
  // ابطال و رفعِ تعلیق. ادمینِ سراسریِ tenant نیست و به مالی/آموزش دسترسی ندارد.
  membership_manager: {
    id: "membership_manager",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.membership_manager", "مدیر برنامهٔ عضویت"); },
    name: "مدیر برنامهٔ عضویت",
    avatar: "مع",
    color: "amber",
    subtitle: "مدیریت برنامهٔ عضویت",
    code: "MBR-100",
    nav: ["admin/membership-queue", "profile", "settings"],
    homeRoute: "admin/membership-queue",
    permissions: ["manage-membership"],
  },
  // Assessor home is the existing assigned-candidate console.
  assessor: {
    id: "assessor",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.assessor", "ارزیاب"); },
    name: "ارزیاب",
    avatar: "ار",
    color: "cyan",
    subtitle: "کنسولِ ارزیابی",
    code: "ASS-001",
    nav: ["assess-center", "profile", "settings"],
    homeRoute: "assess-center",
    permissions: ["manage-assessment"],
  },
  // S-ASSESS-W3b — «مدیر کانونِ ارزیابی». نظارت/washup فراتر از یک ارزیابِ منفرد؛
  // استقلالِ ارزیاب‌ها را بای‌پس نمی‌کند (assess-scope.ts). کنسول + کانونِ مدیریت
  // خانه، کنسول ارزیابی است و نمای مدیریتی از منو در دسترس می‌ماند.
  assessment_manager: {
    id: "assessment_manager",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.assessment_manager", "مدیر کانونِ ارزیابی"); },
    name: "مدیر کانونِ ارزیابی",
    avatar: "مک",
    color: "amber",
    subtitle: "مدیریتِ کانونِ ارزیابی",
    code: "ASS-100",
    nav: ["assess-center", "profile", "settings"],
    homeRoute: "assess-center",
    permissions: ["manage-assessment"],
  },
  super_admin: {
    id: "super_admin",
    // S-I18N-W1a — a getter, not a literal: `role.label` is read by ~39 call
    // sites, so resolving it through the translator seam makes all of them
    // bilingual without touching one. Persian stays the fallback, so a render
    // before i18next attaches is byte-identical to before.
    get label(): string { return translate("shell:roles.super_admin", "ابرمدیر"); },
    name: "علی هاشمی",
    avatar: "AH",
    color: "violet",
    subtitle: "ابرمدیر · بین‌تنانتی",
    code: "SA-001",
    nav: ["home", "admin", "analytics", "audit", "tutor", "events"],
    // Phase-A R3: dedicated Super Admin overview at /super.
    homeRoute: "super",
    permissions: [
      "manage-users",
      "moderate",
      "configure",
      "billing",
      "audit-read",
      "cross-tenant",
    ],
  },
};

export interface RoleContextValue {
  /**
   * The active role's catalogue entry. NON-NULL for layout convenience
   * (colour token, Persian label, homeRoute) — 24 consumers read it
   * without a null-check. For an anonymous visitor it falls back to the
   * `student` entry as a NEUTRAL default; consumers MUST NOT treat this
   * as identity. The canonical "is anyone signed in / which role" signal
   * is `activeRoleId` (null when anonymous). Identity-displaying chrome
   * gates on `isAuthenticated`.
   */
  role: Role;
  /** Active role id, or null when anonymous. The real source of truth. */
  activeRoleId: RoleId | null;
  /** All roles the signed-in user holds, ordered by privilege ([0] = primary). */
  availableRoles: RoleId[];
  /** Mirror of `useAuth().isAuthenticated` for one-stop gating. */
  isAuthenticated: boolean;
  /**
   * Switch the active role — ONLY among `availableRoles` (a multi-role
   * user toggling their workspace, spec §3.1.6). Persisted as a
   * preference; ignored at compute time if not in `availableRoles`, so
   * it can never elevate privilege.
   */
  setRole: (id: RoleId) => void;
}

export const RoleContext = React.createContext<RoleContextValue>({
  role: ROLES.student,
  activeRoleId: null,
  availableRoles: [],
  isAuthenticated: false,
  // Default no-op — overridden by RoleProvider; only triggered if a
  // consumer renders outside the provider (which is a bug).
  setRole: () => {},
});

export const useRole = (): RoleContextValue => React.useContext(RoleContext);

// localStorage now holds at most an ACTIVE-ROLE PREFERENCE (which of a
// multi-role user's roles is active), not the role itself. Key kept as
// `digiu_role` for back-compat with the logout cleanup already in the
// navbar; semantics changed from "the role" to "the preferred role".
const STORAGE_KEY = "digiu_role";

// Set-based check is cheaper to extend than the chained || from the
// pre-R7 5-role world.
const VALID_ROLE_IDS: ReadonlySet<RoleId> = new Set<RoleId>([
  "student",
  "instructor",
  "admin",
  "faculty_admin",
  "parent",
  "org",
  "ta",
  "content_manager",
  "support",
  "moderator",
  "super_admin",
  "finance_manager",
  "counselor",
  "membership_operator",
  "membership_manager",
  "assessor",
  "assessment_manager",
]);

/** Read the persisted active-role PREFERENCE, or null if absent/invalid. */
const readPersistedOverride = (): RoleId | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && VALID_ROLE_IDS.has(raw as RoleId)) {
      return raw as RoleId;
    }
  } catch {
    // localStorage may be blocked (Safari private mode, iframe); fall through.
  }
  return null;
};

export interface RoleProviderProps {
  children: React.ReactNode;
}

export const RoleProvider = ({ children }: RoleProviderProps) => {
  const { user, isAuthenticated } = useAuth();

  // Roles the session actually grants, ordered by privilege. [0] is the
  // primary surface (identical to apiRoleToLocal). Empty when anonymous.
  const availableRoles = React.useMemo<RoleId[]>(
    () => apiRolesToLocal(user?.roles),
    [user?.roles],
  );

  // The active-role PREFERENCE (multi-role switcher). A preference only —
  // the effective role is always validated against availableRoles below.
  const [override, setOverride] = React.useState<RoleId | null>(readPersistedOverride);

  // Effective active role: derived purely from the session each render.
  // - anonymous            → null (no identity)
  // - preference is valid  → the preferred role
  // - else                 → primary (availableRoles[0]); authenticated
  //   users with no recognised role fall back to "student" (matches
  //   apiRoleToLocal's safe default rather than stranding them with null).
  const activeRoleId: RoleId | null = !isAuthenticated
    ? null
    : override && availableRoles.includes(override)
      ? override
      : availableRoles[0] ?? "student";

  // Reconcile the persisted preference with the live session. This is the
  // spec §2.7-1 "rebuild role from session on reload" effect: on logout
  // the preference is cleared; a stale preference from a previous user
  // (not in this session's roles) is dropped.
  React.useEffect(() => {
    if (!isAuthenticated) {
      if (override !== null) setOverride(null);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    } else if (override && !availableRoles.includes(override)) {
      setOverride(null);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  }, [isAuthenticated, availableRoles, override]);

  const setRole = React.useCallback((id: RoleId) => {
    // Record the preference. activeRoleId only honours it if `id` is in
    // availableRoles at render time, so this can never grant privilege.
    if (id in ROLES) {
      setOverride(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        // ignore — same reason as readPersistedOverride
      }
    }
  }, []);

  const value = React.useMemo<RoleContextValue>(
    () => ({
      role: activeRoleId ? ROLES[activeRoleId] : ROLES.student,
      activeRoleId,
      availableRoles,
      isAuthenticated,
      setRole,
    }),
    [activeRoleId, availableRoles, isAuthenticated, setRole],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};
